import axios from 'axios';

const BASE_URL = 'https://loyalty.bom.asia';
const API_BASE_URL = `${BASE_URL}/api/miniApp/quiz/dynamic-form`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const dynamicFormFieldApi = {
  // Get all fields with filters
  getAllFields: async (params = {}) => {
    try {
      
      // Build query string
      const queryParams = new URLSearchParams();
      
      // Pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Filters
      if (params.fieldType) queryParams.append('fieldType', params.fieldType);
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
      
      const queryString = queryParams.toString();
      const url = queryString ? `/admin/fields?${queryString}` : '/admin/fields';
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting all fields:', error);
      throw error;
    }
  },

  // Get field by ID
  getFieldById: async (id) => {
    try {
      const response = await api.get(`/admin/fields/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting field by ID:', error);
      throw error;
    }
  },

  // Create field
  createField: async (fieldData) => {
    try {
      const response = await api.post('/admin/fields', fieldData);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating field:', error);
      throw error;
    }
  },

  // Update field
  updateField: async (id, updateData) => {
    try {
      const response = await api.put(`/admin/fields/${id}`, updateData);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating field:', error);
      throw error;
    }
  },

  // Delete field
  deleteField: async (id) => {
    try {
      const response = await api.delete(`/admin/fields/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting field:', error);
      throw error;
    }
  },

  // Reorder fields
  reorderFields: async (fieldOrders) => {
    try {
      const response = await api.post('/admin/fields/reorder', { fieldOrders });
      return response.data;
    } catch (error) {
      console.error('❌ Error reordering fields:', error);
      throw error;
    }
  },

  // Helper: Get fields by type
  getFieldsByType: async (fieldType) => {
    try {
      const response = await api.get(`/admin/fields?fieldType=${fieldType}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting fields by type:', error);
      throw error;
    }
  },

  // Helper: Get active fields only
  getActiveFields: async () => {
    try {
      const response = await api.get('/admin/fields?isActive=true');
      return response.data;
    } catch (error) {
      console.error('❌ Error getting active fields:', error);
      throw error;
    }
  },

  // Helper: Get text fields
  getTextFields: async () => {
    return dynamicFormFieldApi.getFieldsByType('text');
  },

  // Helper: Get email fields
  getEmailFields: async () => {
    return dynamicFormFieldApi.getFieldsByType('email');
  },

  // Helper: Get select fields
  getSelectFields: async () => {
    return dynamicFormFieldApi.getFieldsByType('select');
  },

  // Helper: Get checkbox fields
  getCheckboxFields: async () => {
    return dynamicFormFieldApi.getFieldsByType('checkbox');
  },

  // Helper: Get radio fields
  getRadioFields: async () => {
    return dynamicFormFieldApi.getFieldsByType('radio');
  },

  // Helper: Get textarea fields
  getTextareaFields: async () => {
    return dynamicFormFieldApi.getFieldsByType('textarea');
  },

  // Helper: Get number fields
  getNumberFields: async () => {
    return dynamicFormFieldApi.getFieldsByType('number');
  },

  // Helper: Get date fields
  getDateFields: async () => {
    return dynamicFormFieldApi.getFieldsByType('date');
  },

  // Helper: Create text field
  createTextField: async (fieldData) => {
    return dynamicFormFieldApi.createField({
      ...fieldData,
      fieldType: 'text'
    });
  },

  // Helper: Create email field
  createEmailField: async (fieldData) => {
    return dynamicFormFieldApi.createField({
      ...fieldData,
      fieldType: 'email'
    });
  },

  // Helper: Create select field
  createSelectField: async (fieldData) => {
    return dynamicFormFieldApi.createField({
      ...fieldData,
      fieldType: 'select'
    });
  },

  // Helper: Create checkbox field
  createCheckboxField: async (fieldData) => {
    return dynamicFormFieldApi.createField({
      ...fieldData,
      fieldType: 'checkbox'
    });
  }
};

export default dynamicFormFieldApi;

